"use client";

import { useRef } from "react";
import dynamic from "next/dynamic";
import "react-quill-new/dist/quill.snow.css";

// Quill accesses `document` on load — must be dynamically imported with SSR disabled.
const ReactQuill = dynamic(() => import("react-quill-new"), { ssr: false });

const FORMATS = ["bold", "underline", "link"];
const MODULES = { toolbar: { container: [["bold", "underline", "link"]] } };

interface AnnouncementQuillEditorProps {
    value: string;
    maxChars: number;
    onChange: (html: string, textLength: number) => void;
}

const AnnouncementQuillEditor = (props: AnnouncementQuillEditorProps) => {
    const { value, maxChars, onChange } = props;
    // Used to revert count when limit was exceed
    const lastTextLength = useRef(0);

    const handleChange = (
        html: string,
        _delta: unknown,
        _source: unknown,
        editor: { getLength: () => number }
    ) => {
        // getLength() includes Quill's trailing newline — subtract 1 for the real count.
        const textLength = editor.getLength() - 1;
        if (textLength > maxChars) {
            onChange(value, lastTextLength.current);
            return;
        }
        lastTextLength.current = textLength;
        onChange(html, textLength);
    };

    return (
        // Animates height from 0 to full when loaded so it does not stutter caused by the SSR stuff
        <div className="grid grid-rows-[0fr] has-[.ql-editor]:grid-rows-[1fr] transition-[grid-template-rows] duration-300 ease-out">
            <div className="min-h-0">
                <ReactQuill
                    className="[&_.ql-toolbar]:rounded-t-md [&_.ql-container]:rounded-b-md [&_.ql-tooltip]:z-50 [&_.ql-tooltip]:!left-0 [&_.ql-toolbar_button:not(.ql-active)_.ql-stroke]:!stroke-default [&_.ql-toolbar_button.ql-active_.ql-stroke]:!stroke-primary-default [&_.ql-toolbar]:!border-surface-20 [&_.ql-container]:!border-surface-20 [&:hover_.ql-toolbar]:!border-primary-default [&:hover_.ql-container]:!border-primary-default [&_.ql-editor]:break-all [&_.ql-toolbar]:h-12 [&_.ql-editor]:h-12 [&_.ql-toolbar_button_.ql-stroke]:transition-[stroke] [&_.ql-toolbar_button_.ql-stroke]:duration-200"
                    value={value}
                    onChange={handleChange}
                    formats={FORMATS}
                    modules={MODULES}
                />
            </div>
        </div>
    );
};

export default AnnouncementQuillEditor;
